import React, { useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { Button } from '../../../components/new-components/ui/button';
import { Input } from '../../../components/new-components/ui/input';
import { Label } from '../../../components/new-components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/new-components/ui/select';

export interface BrandingData {
  logoFile: File | null;
  logoPreviewUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
}

interface SandboxBrandingStepProps {
  companyName: string;
  branding: BrandingData;
  onChange: (update: Partial<BrandingData>) => void;
  onContinue: () => void;
  onBack: () => void;
}

const FONT_OPTIONS = ['Inter', 'Montserrat', 'DM Sans', 'Poppins'] as const;

const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB

export function SandboxBrandingStep({
  companyName,
  branding,
  onChange,
  onContinue,
  onBack,
}: SandboxBrandingStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setLogoError(null);
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setLogoError('File must be an image.');
      return;
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setLogoError('Logo must be under 2 MB.');
      return;
    }
    const url = URL.createObjectURL(file);
    onChange({ logoFile: file, logoPreviewUrl: url });
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0] ?? null;
    if (!file) return;
    // Re-use the same validation
    const fakeEvent = { target: { files: e.dataTransfer.files } } as unknown as React.ChangeEvent<HTMLInputElement>;
    handleLogoChange(fakeEvent);
  }

  function clearLogo() {
    onChange({ logoFile: null, logoPreviewUrl: null });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleHexInput(field: 'primaryColor' | 'secondaryColor', value: string) {
    // Accept partial input; normalise on blur
    const hex = value.startsWith('#') ? value : `#${value}`;
    onChange({ [field]: hex });
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">Brand your sandbox</h2>
        <p className="mt-1 text-muted-foreground">
          Upload a logo and choose colours. Investors will see this when they accept their invite.
        </p>
      </div>

      <div className="space-y-6">
        {/* Logo Upload */}
        <div className="space-y-2">
          <Label>Logo (optional)</Label>
          {branding.logoPreviewUrl ? (
            <div className="flex items-center gap-4">
              <div className="w-[120px] h-[60px] rounded-lg border border-border overflow-hidden bg-muted flex items-center justify-center">
                <img
                  src={branding.logoPreviewUrl}
                  alt="Logo preview"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <Button variant="ghost" size="sm" onClick={clearLogo} className="text-muted-foreground">
                <X className="w-4 h-4 mr-1" />
                Remove
              </Button>
            </div>
          ) : (
            <div
              className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/60 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Drag and drop or{' '}
                <span className="text-primary font-medium">click to upload</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">PNG, JPG, SVG — max 2 MB</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleLogoChange}
          />
          {logoError && <p className="text-xs text-red-500">{logoError}</p>}
        </div>

        {/* Primary Color */}
        <div className="space-y-2">
          <Label>Primary Color</Label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={branding.primaryColor}
              onChange={(e) => onChange({ primaryColor: e.target.value })}
              className="w-10 h-10 rounded-md border border-border cursor-pointer p-0.5 bg-background"
            />
            <Input
              value={branding.primaryColor}
              onChange={(e) => handleHexInput('primaryColor', e.target.value)}
              className="w-32 font-mono"
              placeholder="#000000"
              maxLength={7}
            />
          </div>
        </div>

        {/* Secondary Color */}
        <div className="space-y-2">
          <Label>Secondary Color</Label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={branding.secondaryColor}
              onChange={(e) => onChange({ secondaryColor: e.target.value })}
              className="w-10 h-10 rounded-md border border-border cursor-pointer p-0.5 bg-background"
            />
            <Input
              value={branding.secondaryColor}
              onChange={(e) => handleHexInput('secondaryColor', e.target.value)}
              className="w-32 font-mono"
              placeholder="#000000"
              maxLength={7}
            />
          </div>
        </div>

        {/* Font Family */}
        <div className="space-y-2">
          <Label>Font Family</Label>
          <Select
            value={branding.fontFamily}
            onValueChange={(val) => onChange({ fontFamily: val })}
          >
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Select font" />
            </SelectTrigger>
            <SelectContent>
              {FONT_OPTIONS.map((font) => (
                <SelectItem key={font} value={font}>
                  {font}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Preview Card */}
        <div className="space-y-2">
          <Label>Preview</Label>
          <div
            className="rounded-xl border border-border p-5 flex items-center gap-4"
            style={{ backgroundColor: branding.secondaryColor + '22' }}
          >
            {branding.logoPreviewUrl ? (
              <div className="w-[80px] h-[40px] flex-shrink-0 rounded overflow-hidden bg-white flex items-center justify-center border border-border/40">
                <img
                  src={branding.logoPreviewUrl}
                  alt="Logo"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ) : (
              <div
                className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: branding.primaryColor }}
              >
                {companyName.charAt(0).toUpperCase() || '?'}
              </div>
            )}
            <div>
              <p
                className="text-lg font-semibold"
                style={{
                  fontFamily: branding.fontFamily,
                  color: branding.primaryColor,
                }}
              >
                {companyName || 'Your Company Name'}
              </p>
              <p
                className="text-sm"
                style={{ fontFamily: branding.fontFamily, color: branding.secondaryColor }}
              >
                Investor Portal
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1">
          Back
        </Button>
        <Button onClick={onContinue} className="flex-1">
          Continue
        </Button>
      </div>
    </div>
  );
}
